import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  FileAudio, 
  Plus, 
  Pencil, 
  Trash2, 
  PlayCircle,
  Info,
  ArrowRight,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Settings,
  UserCheck,
  Search,
  Zap
} from 'lucide-react';
import { 
  getAudioDecodingFormats, 
  createAudioDecodingFormat, 
  updateAudioDecodingFormat, 
  deleteAudioDecodingFormat,
  parseFilenameWithFormat,
  seedDefaultFormats,
  getCallAssignmentConfig,
  updateCallAssignmentConfig,
  resetCallAssignmentConfig,
  testCallAssignmentMatch,
  refreshAllCallsAssignment,
  syncUnknownCallsWithUsers
} from '@/lib/api';
import { toast } from 'sonner';

// Segment types that can be used in decoding formats
const SEGMENT_TYPES = [
  { value: 'date', label: 'Date', description: 'Call date (YYYY_MM_DD)' },
  { value: 'time', label: 'Time', description: 'Call time (HH_MMAM/PM)' },
  { value: 'applicant_phone', label: 'Applicant Phone', description: 'Applicant/Candidate phone number' },
  { value: 'user_phone', label: 'User Phone', description: 'User direct phone number' },
  { value: 'extension', label: 'Extension', description: 'User extension number' },
  { value: 'user_name', label: 'User Name', description: 'User first name' },
  { value: 'applicant_id', label: 'Applicant ID', description: 'Applicant/Candidate ID' },
  { value: 'custom', label: 'Custom', description: 'Custom field' },
];

const CALL_TYPES = [
  { value: 'outbound', label: 'Outbound', icon: PhoneOutgoing, color: 'text-blue-500' },
  { value: 'inbound', label: 'Inbound', icon: PhoneIncoming, color: 'text-green-500' },
];

// Matching strategies for call assignment
const MATCHING_STRATEGIES = [
  { value: 'phone_only', label: 'Phone Only', description: 'Match only by phone number' },
  { value: 'extension_only', label: 'Extension Only', description: 'Match only by extension' },
  { value: 'phone_or_extension', label: 'Phone or Extension', description: 'Match by either phone or extension' },
  { value: 'transcript_search', label: 'Transcript Search', description: 'Search transcription for identifiers' },
  { value: 'filename_parse', label: 'Filename Parse', description: 'Parse filename for identifiers' },
  { value: 'all', label: 'All Strategies', description: 'Use all available matching methods' },
];

// Sample filenames for testing
const SAMPLE_FILENAMES = [
  { filename: '2025_05_02_04_59PM_17343730051_14018244204.mp3', description: 'Inbound (7 parts) - No extension' },
  { filename: '2025_05_02_04_59PM_17343730051_14018244204_554.mp3', description: 'Inbound (8 parts) - With extension' },
  { filename: '2025_12_01_12_50PM_13103508777_524_Madhuri.mp3', description: 'Outbound - With user name' },
  { filename: '2025_01_15_09_30AM_9876543210_123.mp3', description: 'Outbound - Without user name' },
];

// Format Tester Component
function FormatTesterCard({ formats, parseFilenameWithFormat, testCallAssignmentMatch }) {
  const [filename, setFilename] = useState('');
  const [parseResult, setParseResult] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [autoTest, setAutoTest] = useState(true);

  // Parse filename when it changes (with debounce)
  useEffect(() => {
    if (!filename || !autoTest) return;
    
    const timer = setTimeout(async () => {
      setIsParsing(true);
      try {
        const { data } = await parseFilenameWithFormat(filename);
        setParseResult(data);
      } catch (error) {
        setParseResult({ error: 'Failed to parse filename' });
      } finally {
        setIsParsing(false);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [filename, autoTest, parseFilenameWithFormat]);

  const handleManualParse = async () => {
    if (!filename) {
      toast.error('Please enter a filename');
      return;
    }
    setIsParsing(true);
    try {
      const { data } = await parseFilenameWithFormat(filename);
      setParseResult(data);
    } catch (error) {
      setParseResult({ error: 'Failed to parse filename' });
    } finally {
      setIsParsing(false);
    }
  };

  const handleTestMatch = async () => {
    if (!filename) {
      toast.error('Please enter a filename');
      return;
    }
    setIsMatching(true);
    try {
      const { data } = await testCallAssignmentMatch({ filename });
      setMatchResult(data);
    } catch (error) {
      setMatchResult({ error: 'Failed to test match' });
    } finally {
      setIsMatching(false);
    }
  };

  const handleSampleClick = (sample) => {
    setFilename(sample.filename);
    setParseResult(null);
    setMatchResult(null);
  };

  const getFilenameParts = () => {
    if (!filename) return [];
    const base = filename.replace(/\.[^.]+$/, '');
    return base.split('_');
  };

  const parts = getFilenameParts();

  return (
    <Card className="card-elevated">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg">
            <Zap className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="font-heading">Audio Format Tester</CardTitle>
            <CardDescription>
              Test how filenames are parsed and matched to users instantly
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Input Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">Enter Filename</Label>
            <div className="flex items-center gap-2">
              <Switch 
                checked={autoTest} 
                onCheckedChange={setAutoTest}
                id="auto-test"
              />
              <Label htmlFor="auto-test" className="text-sm text-muted-foreground cursor-pointer">
                Auto-parse as you type
              </Label>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="Paste or type a filename here..."
              className="flex-1 text-base font-mono"
            />
            {!autoTest && (
              <Button onClick={handleManualParse} disabled={isParsing}>
                {isParsing ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Parse'}
              </Button>
            )}
          </div>
        </div>

        {/* Sample Filenames */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Try these sample filenames:</Label>
          <div className="flex flex-wrap gap-2">
            {SAMPLE_FILENAMES.map((sample, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                className="text-xs h-auto py-1.5 font-mono"
                onClick={() => handleSampleClick(sample)}
                title={sample.description}
              >
                {sample.description}
              </Button>
            ))}
          </div>
        </div>

        {/* Filename Parts Visualization */}
        {parts.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Filename breakdown ({parts.length} parts):</Label>
            <div className="flex flex-wrap gap-1 p-3 bg-muted/50 rounded-lg font-mono text-sm">
              {parts.map((part, idx) => (
                <span key={idx} className="flex items-center">
                  <span className={`px-2 py-1 rounded ${
                    idx < 3 ? 'bg-blue-100 text-blue-700' : 
                    idx < 5 ? 'bg-purple-100 text-purple-700' : 
                    'bg-green-100 text-green-700'
                  }`}>
                    {part}
                  </span>
                  {idx < parts.length - 1 && (
                    <span className="text-muted-foreground mx-0.5">_</span>
                  )}
                </span>
              ))}
              <span className="text-muted-foreground">.mp3</span>
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-blue-100"></span> Date parts
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-purple-100"></span> Time parts
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-green-100"></span> Data parts
              </span>
            </div>
          </div>
        )}

        {/* Parse Result */}
        {parseResult && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Parse Result</Label>
              {isParsing && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            
            {parseResult.error ? (
              <div className="flex items-center gap-2 p-4 bg-red-500/10 dark:bg-red-500/20 border border-red-400 dark:border-red-600 rounded-lg text-red-700 dark:text-red-400">
                <XCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">No matching format found</p>
                  <p className="text-sm mt-1">This filename does not match any active audio decoding format. Check if:
                  </p>
                  <ul className="text-sm mt-1 list-disc list-inside">
                    <li>The number of parts matches a configured format</li>
                    <li>The format is marked as active</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-green-500/10 dark:bg-green-500/20 border border-green-400 dark:border-green-600 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400 mb-3">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Matched: {parseResult.format_used}</span>
                  <Badge variant="outline" className={`ml-auto ${
                    parseResult.call_type === 'inbound' ? 'border-green-500 text-green-700 dark:text-green-400' : 'border-blue-500 text-blue-700 dark:text-blue-400'
                  }`}>
                    {parseResult.call_type === 'inbound' ? (
                      <><PhoneIncoming className="h-3 w-3 mr-1" /> Inbound</>
                    ) : (
                      <><PhoneOutgoing className="h-3 w-3 mr-1" /> Outbound</>
                    )}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {parseResult.date && (
                    <div className="p-2 bg-muted/50 rounded border border-border">
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="font-medium">{parseResult.date}</p>
                    </div>
                  )}
                  {parseResult.time && (
                    <div className="p-2 bg-muted/50 rounded border border-border">
                      <p className="text-xs text-muted-foreground">Time</p>
                      <p className="font-medium">{parseResult.time}</p>
                    </div>
                  )}
                  {parseResult.user_phone && (
                    <div className="p-2 bg-green-500/10 dark:bg-green-500/20 rounded border border-green-400 dark:border-green-600">
                      <p className="text-xs text-green-600 dark:text-green-400">User Phone</p>
                      <p className="font-medium font-mono">{parseResult.user_phone}</p>
                    </div>
                  )}
                  {parseResult.applicant_phone && (
                    <div className="p-2 bg-muted/50 rounded border border-border">
                      <p className="text-xs text-muted-foreground">Applicant Phone</p>
                      <p className="font-medium font-mono">{parseResult.applicant_phone}</p>
                    </div>
                  )}
                  {parseResult.extension && (
                    <div className="p-2 bg-blue-500/10 dark:bg-blue-500/20 rounded border border-blue-400 dark:border-blue-600">
                      <p className="text-xs text-blue-600 dark:text-blue-400">Extension</p>
                      <p className="font-medium font-mono">{parseResult.extension}</p>
                    </div>
                  )}
                  {parseResult.user_name && (
                    <div className="p-2 bg-purple-500/10 dark:bg-purple-500/20 rounded border border-purple-400 dark:border-purple-600">
                      <p className="text-xs text-purple-600 dark:text-purple-400">User Name</p>
                      <p className="font-medium">{parseResult.user_name}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Match Test */}
        {parseResult && !parseResult.error && (
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Test User Matching</Label>
                <p className="text-sm text-muted-foreground">See which user would be assigned to this call</p>
              </div>
              <Button onClick={handleTestMatch} disabled={isMatching} variant="outline">
                {isMatching ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Find User Match
              </Button>
            </div>

            {matchResult && (
              <div className={`p-4 rounded-lg border ${
                matchResult.match_found 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {matchResult.match_found ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-green-700">User Found!</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-amber-600" />
                      <span className="font-medium text-amber-700">No User Match</span>
                    </>
                  )}
                </div>
                
                {matchResult.match_found && matchResult.match_result && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-muted-foreground">User Name</p>
                      <p className="font-medium">{matchResult.match_result.user_name}</p>
                    </div>
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-muted-foreground">Match Type</p>
                      <p className="font-medium">{matchResult.match_result.match_type}</p>
                    </div>
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-muted-foreground">Matched Value</p>
                      <p className="font-medium font-mono">{matchResult.match_result.match_value}</p>
                    </div>
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-muted-foreground">Confidence</p>
                      <p className="font-medium">{(matchResult.match_result.confidence * 100).toFixed(0)}%</p>
                    </div>
                  </div>
                )}
                
                {!matchResult.match_found && (
                  <p className="text-sm text-amber-700 mt-2">
                    No user has a matching phone number or extension. Make sure users have their contact info configured.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Active Formats Reference */}
        <div className="pt-4 border-t">
          <Label className="text-sm text-muted-foreground mb-2 block">Active Formats ({formats.filter(f => f.is_active).length})</Label>
          <div className="flex flex-wrap gap-2">
            {formats.filter(f => f.is_active).map((fmt) => (
              <Badge key={fmt.id} variant="secondary" className="font-mono text-xs">
                {fmt.name} ({fmt.segments?.reduce((sum, s) => sum + (s.parts || 1), 0) || 0} parts)
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AudioDecodingSettings() {
  const [activeTab, setActiveTab] = useState('tester');
  const [formats, setFormats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [editingFormat, setEditingFormat] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    call_type: 'outbound',
    pattern_description: '',
    segments: [],
    example_filename: ''
  });
  
  // Test state
  const [testFilename, setTestFilename] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  
  // Call Assignment Config state
  const [assignmentConfig, setAssignmentConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [testMatchData, setTestMatchData] = useState({
    phone_number: '',
    extension: '',
    filename: '',
    transcript_text: ''
  });
  const [testMatchResult, setTestMatchResult] = useState(null);
  const [isTestingMatch, setIsTestingMatch] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  useEffect(() => {
    fetchFormats();
    fetchAssignmentConfig();
  }, []);

  const fetchFormats = async () => {
    try {
      const { data } = await getAudioDecodingFormats();
      setFormats(data);
    } catch (error) {
      console.error('Failed to fetch formats:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchAssignmentConfig = async () => {
    setIsLoadingConfig(true);
    try {
      const { data } = await getCallAssignmentConfig();
      setAssignmentConfig(data);
    } catch (error) {
      console.error('Failed to fetch assignment config:', error);
    } finally {
      setIsLoadingConfig(false);
    }
  };
  
  const handleRefreshAllCalls = async () => {
    if (!window.confirm('This will re-run the matching engine on ALL calls. Calls may be reassigned to different users. Continue?')) return;
    setIsRefreshing(true);
    setRefreshResult(null);
    try {
      const { data } = await refreshAllCallsAssignment();
      setRefreshResult(data);
      toast.success(`Refreshed ${data.total_calls_processed} calls. ${data.updated_count} updated.`);
    } catch (error) {
      toast.error('Failed to refresh calls');
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const handleSyncUnknownCalls = async () => {
    setIsRefreshing(true);
    try {
      const { data } = await syncUnknownCallsWithUsers();
      toast.success(`Synced unknown calls. ${data.matched_count} matched.`);
      setRefreshResult(data);
    } catch (error) {
      toast.error('Failed to sync unknown calls');
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const handleSaveConfig = async () => {
    setIsSavingConfig(true);
    try {
      await updateCallAssignmentConfig(assignmentConfig);
      toast.success('Configuration saved successfully');
      fetchAssignmentConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setIsSavingConfig(false);
    }
  };
  
  const handleResetConfig = async () => {
    if (!window.confirm('Are you sure you want to reset to default configuration?')) return;
    try {
      await resetCallAssignmentConfig();
      toast.success('Configuration reset to defaults');
      fetchAssignmentConfig();
    } catch (error) {
      toast.error('Failed to reset configuration');
    }
  };
  
  const handleTestMatch = async () => {
    setIsTestingMatch(true);
    try {
      const params = {};
      if (testMatchData.phone_number) params.phone_number = testMatchData.phone_number;
      if (testMatchData.extension) params.extension = testMatchData.extension;
      if (testMatchData.filename) params.filename = testMatchData.filename;
      if (testMatchData.transcript_text) params.transcript_text = testMatchData.transcript_text;
      
      const { data } = await testCallAssignmentMatch(params);
      setTestMatchResult(data);
    } catch (error) {
      toast.error('Failed to test match');
    } finally {
      setIsTestingMatch(false);
    }
  };
  
  const updateConfigField = (field, value) => {
    setAssignmentConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const toggleStrategy = (strategy) => {
    const current = assignmentConfig?.enabled_strategies || [];
    const updated = current.includes(strategy)
      ? current.filter(s => s !== strategy)
      : [...current, strategy];
    updateConfigField('enabled_strategies', updated);
  };

  const handleSeedDefaults = async () => {
    try {
      await seedDefaultFormats();
      toast.success('Default formats seeded successfully');
      fetchFormats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed defaults');
    }
  };

  const openCreateDialog = () => {
    setEditingFormat(null);
    setFormData({
      name: '',
      call_type: 'outbound',
      pattern_description: '',
      segments: [],
      example_filename: ''
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (format) => {
    setEditingFormat(format);
    setFormData({
      name: format.name,
      call_type: format.call_type,
      pattern_description: format.pattern_description,
      segments: format.segments || [],
      example_filename: format.example_filename
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.example_filename || formData.segments.length === 0) {
      toast.error('Please fill in all required fields and add at least one segment');
      return;
    }

    setIsSaving(true);
    try {
      if (editingFormat) {
        await updateAudioDecodingFormat(editingFormat.id, formData);
        toast.success('Format updated successfully');
      } else {
        await createAudioDecodingFormat(formData);
        toast.success('Format created successfully');
      }
      setIsDialogOpen(false);
      fetchFormats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save format');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (formatId) => {
    if (!window.confirm('Are you sure you want to delete this format?')) return;
    
    try {
      await deleteAudioDecodingFormat(formatId);
      toast.success('Format deleted');
      fetchFormats();
    } catch (error) {
      toast.error('Failed to delete format');
    }
  };

  const handleToggleActive = async (format) => {
    try {
      await updateAudioDecodingFormat(format.id, { is_active: !format.is_active });
      toast.success(`Format ${format.is_active ? 'deactivated' : 'activated'}`);
      fetchFormats();
    } catch (error) {
      toast.error('Failed to update format');
    }
  };

  const addSegment = () => {
    setFormData(prev => ({
      ...prev,
      segments: [...prev.segments, { type: 'date', parts: 3, description: '' }]
    }));
  };

  const updateSegment = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      segments: prev.segments.map((seg, i) => 
        i === index ? { ...seg, [field]: value } : seg
      )
    }));
  };

  const removeSegment = (index) => {
    setFormData(prev => ({
      ...prev,
      segments: prev.segments.filter((_, i) => i !== index)
    }));
  };

  const handleTest = async () => {
    if (!testFilename) {
      toast.error('Please enter a filename to test');
      return;
    }

    setIsTesting(true);
    try {
      const { data } = await parseFilenameWithFormat(testFilename);
      setTestResult(data);
    } catch (error) {
      setTestResult({ error: 'Failed to parse filename' });
    } finally {
      setIsTesting(false);
    }
  };

  const getCallTypeIcon = (type) => {
    const callType = CALL_TYPES.find(t => t.value === type);
    if (!callType) return <Phone className="h-4 w-4" />;
    const Icon = callType.icon;
    return <Icon className={`h-4 w-4 ${callType.color}`} />;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Settings className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audio & Call Settings</h1>
          <p className="text-muted-foreground">Configure audio decoding formats and call assignment rules</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="formats" className="flex items-center gap-2">
            <FileAudio className="h-4 w-4" />
            Audio Decoding Formats
          </TabsTrigger>
          <TabsTrigger value="tester" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Format Tester
          </TabsTrigger>
          <TabsTrigger value="assignment" className="flex items-center gap-2">
            <UserCheck className="h-4 w-4" />
            Call Assignment Config
          </TabsTrigger>
        </TabsList>

        {/* Format Tester Tab - NEW */}
        <TabsContent value="tester">
          <FormatTesterCard 
            formats={formats}
            parseFilenameWithFormat={parseFilenameWithFormat}
            testCallAssignmentMatch={testCallAssignmentMatch}
          />
        </TabsContent>

        {/* Audio Decoding Formats Tab */}
        <TabsContent value="formats">
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Audio Decoding Formats</CardTitle>
                  <CardDescription>
                    Configure how audio filenames are parsed to extract call metadata
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsTestDialogOpen(true)}>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Test Parser
                  </Button>
                  <Button onClick={openCreateDialog} className="btn-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Format
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">How Audio Decoding Works</p>
                    <p>
                      Audio filenames contain valuable metadata about calls. By defining decoding formats,
                      the system can automatically extract date, time, phone numbers, user extensions, and more
                      from uploaded audio files. This helps align calls to the correct users and applicants.
                    </p>
                  </div>
                </div>
              </div>

              {/* Formats Table */}
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading formats...</div>
              ) : formats.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                  <FileAudio className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No Formats Configured</h3>
                  <p className="text-muted-foreground mb-4">Add your first audio decoding format or seed the defaults</p>
                  <div className="flex items-center justify-center gap-3">
                    <Button variant="outline" onClick={handleSeedDefaults}>
                      <Download className="h-4 w-4 mr-2" />
                      Seed Default Formats
                    </Button>
                    <Button onClick={openCreateDialog} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Format
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Format Name</TableHead>
                  <TableHead>Call Type</TableHead>
                  <TableHead>Segments</TableHead>
                  <TableHead>Example</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formats.map((format) => (
                  <TableRow key={format.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{format.name}</p>
                        <p className="text-xs text-muted-foreground max-w-xs truncate">
                          {format.pattern_description}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="gap-1">
                        {getCallTypeIcon(format.call_type)}
                        {format.call_type === 'outbound' ? 'Outbound' : 'Inbound'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {format.segments?.slice(0, 4).map((seg, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {seg.type}
                          </Badge>
                        ))}
                        {format.segments?.length > 4 && (
                          <Badge variant="secondary" className="text-xs">
                            +{format.segments.length - 4}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded max-w-[200px] block truncate">
                        {format.example_filename}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={format.is_active}
                        onCheckedChange={() => handleToggleActive(format)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(format)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(format.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </TabsContent>

      {/* Call Assignment Configuration Tab */}
      <TabsContent value="assignment">
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">Call Assignment Configuration</CardTitle>
                <CardDescription>
                  Configure how calls are automatically matched to users based on phone numbers, extensions, and transcripts
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleSyncUnknownCalls}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <UserCheck className="h-4 w-4 mr-2" />}
                  Sync Unknown Calls
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRefreshAllCalls}
                  disabled={isRefreshing}
                  className="bg-amber-50 border-amber-200 hover:bg-amber-100"
                >
                  {isRefreshing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2 text-amber-600" />}
                  Refresh All Calls
                </Button>
                <Button variant="outline" size="sm" onClick={handleResetConfig}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset to Defaults
                </Button>
                <Button onClick={handleSaveConfig} disabled={isSavingConfig} className="btn-primary">
                  {isSavingConfig ? 'Saving...' : 'Save Configuration'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Refresh Result */}
            {refreshResult && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-700">Refresh Complete</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Processed:</span>
                    <span className="ml-2 font-medium">{refreshResult.total_calls_processed || refreshResult.total_unknown_processed || 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Matched:</span>
                    <span className="ml-2 font-medium text-green-600">{refreshResult.matched_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Updated:</span>
                    <span className="ml-2 font-medium text-blue-600">{refreshResult.updated_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">No Match:</span>
                    <span className="ml-2 font-medium text-amber-600">{refreshResult.no_match_found || refreshResult.remaining_unknown || 0}</span>
                  </div>
                </div>
                {refreshResult.matched_details?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Recent Matches:</p>
                    <div className="max-h-32 overflow-y-auto">
                      {refreshResult.matched_details.slice(0, 5).map((detail, idx) => (
                        <div key={idx} className="text-xs text-muted-foreground py-1">
                          <span className="font-mono">{detail.filename || detail.call_id}</span>
                          <span className="mx-2">→</span>
                          <span className="font-medium">{detail.new_user || detail.matched_user}</span>
                          <span className="text-muted-foreground ml-2">({detail.match_type}: {detail.match_value})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {isLoadingConfig ? (
              <div className="text-center py-8 text-muted-foreground">Loading configuration...</div>
            ) : assignmentConfig ? (
              <div className="space-y-8">
                {/* TekWissen Format Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-1">TekWissen Filename Format Support</p>
                      <p className="mb-2">The system automatically detects user extension and direct number from filenames:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li><strong>Outbound:</strong> <code className="bg-blue-100 px-1 rounded">Year_Month_Date_Time_ApplicantPhone_<span className="text-green-700 font-bold">UserExtension</span>.mp3</code></li>
                        <li><strong>Inbound:</strong> <code className="bg-blue-100 px-1 rounded">Year_Month_Date_Time_<span className="text-green-700 font-bold">UserDirectNumber</span>_ApplicantPhone_<span className="text-green-700 font-bold">UserExtension</span>.mp3</code></li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Matching Strategies */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Matching Strategies</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Select which methods should be used to match calls to users. The system will try each enabled strategy in order.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {MATCHING_STRATEGIES.map((strategy) => (
                      <div
                        key={strategy.value}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          assignmentConfig.enabled_strategies?.includes(strategy.value)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => toggleStrategy(strategy.value)}
                      >
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={assignmentConfig.enabled_strategies?.includes(strategy.value)}
                            onCheckedChange={() => toggleStrategy(strategy.value)}
                          />
                          <span className="font-medium">{strategy.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{strategy.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Phone Number Matching Options */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Phone Number Matching</Label>
                    <p className="text-sm text-muted-foreground mt-1">Configure how phone numbers are matched</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Match Full Phone</Label>
                        <Switch
                          checked={assignmentConfig.match_full_phone}
                          onCheckedChange={(v) => updateConfigField('match_full_phone', v)}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Match exact phone numbers</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Match Last N Digits</Label>
                      <Input
                        type="number"
                        min={4}
                        max={10}
                        value={assignmentConfig.match_last_n_digits || 7}
                        onChange={(e) => updateConfigField('match_last_n_digits', parseInt(e.target.value))}
                      />
                      <p className="text-xs text-muted-foreground">Match last N digits of phone number</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Extension Digits</Label>
                      <Input
                        type="number"
                        min={3}
                        max={5}
                        value={assignmentConfig.match_extension_digits || 4}
                        onChange={(e) => updateConfigField('match_extension_digits', parseInt(e.target.value))}
                      />
                      <p className="text-xs text-muted-foreground">Number of digits for extension matching</p>
                    </div>
                  </div>
                </div>

                {/* Transcript Search Options */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Transcript Search</Label>
                    <p className="text-sm text-muted-foreground mt-1">Configure what to search for in call transcripts</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label>Search for Phone Numbers</Label>
                        <p className="text-xs text-muted-foreground">Find phone numbers in transcript</p>
                      </div>
                      <Switch
                        checked={assignmentConfig.search_transcript_for_phone}
                        onCheckedChange={(v) => updateConfigField('search_transcript_for_phone', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label>Search for Extensions</Label>
                        <p className="text-xs text-muted-foreground">Find extensions in transcript</p>
                      </div>
                      <Switch
                        checked={assignmentConfig.search_transcript_for_extension}
                        onCheckedChange={(v) => updateConfigField('search_transcript_for_extension', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label>Search for Names</Label>
                        <p className="text-xs text-muted-foreground">Find user names (slower)</p>
                      </div>
                      <Switch
                        checked={assignmentConfig.search_transcript_for_name}
                        onCheckedChange={(v) => updateConfigField('search_transcript_for_name', v)}
                      />
                    </div>
                  </div>
                </div>

                {/* Auto-Assignment Behavior */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Auto-Assignment Behavior</Label>
                    <p className="text-sm text-muted-foreground mt-1">Configure when automatic assignment happens</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label>On Upload</Label>
                        <p className="text-xs text-muted-foreground">Try to assign when call is uploaded</p>
                      </div>
                      <Switch
                        checked={assignmentConfig.auto_assign_on_upload}
                        onCheckedChange={(v) => updateConfigField('auto_assign_on_upload', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label>After Transcription</Label>
                        <p className="text-xs text-muted-foreground">Re-check after transcript is ready</p>
                      </div>
                      <Switch
                        checked={assignmentConfig.auto_assign_on_transcription}
                        onCheckedChange={(v) => updateConfigField('auto_assign_on_transcription', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label>Mark Unmatched as Unknown</Label>
                        <p className="text-xs text-muted-foreground">Flag calls without matches</p>
                      </div>
                      <Switch
                        checked={assignmentConfig.mark_unmatched_as_unknown}
                        onCheckedChange={(v) => updateConfigField('mark_unmatched_as_unknown', v)}
                      />
                    </div>
                  </div>
                </div>

                {/* Test Matching */}
                <div className="space-y-4 pt-6 border-t">
                  <div>
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      Test Matching Engine
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">Test how a call would be matched with the current configuration</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input
                        value={testMatchData.phone_number}
                        onChange={(e) => setTestMatchData(prev => ({ ...prev, phone_number: e.target.value }))}
                        placeholder="e.g., 5551234567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Extension</Label>
                      <Input
                        value={testMatchData.extension}
                        onChange={(e) => setTestMatchData(prev => ({ ...prev, extension: e.target.value }))}
                        placeholder="e.g., 1234"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Filename</Label>
                      <Input
                        value={testMatchData.filename}
                        onChange={(e) => setTestMatchData(prev => ({ ...prev, filename: e.target.value }))}
                        placeholder="e.g., 2025_01_15_ext1234_call.mp3"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Transcript Text</Label>
                      <Input
                        value={testMatchData.transcript_text}
                        onChange={(e) => setTestMatchData(prev => ({ ...prev, transcript_text: e.target.value }))}
                        placeholder="e.g., Hello this is extension 1234"
                      />
                    </div>
                  </div>
                  <Button onClick={handleTestMatch} disabled={isTestingMatch} className="w-full">
                    {isTestingMatch ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                    Test Match
                  </Button>

                  {testMatchResult && (
                    <div className={`p-4 rounded-lg ${testMatchResult.match_found ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {testMatchResult.match_found ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-amber-600" />
                        )}
                        <span className={`font-medium ${testMatchResult.match_found ? 'text-green-700' : 'text-amber-700'}`}>
                          {testMatchResult.match_found ? 'Match Found!' : 'No Match Found'}
                        </span>
                      </div>
                      {testMatchResult.match_found && testMatchResult.match_result && (
                        <div className="text-sm text-green-700 space-y-1">
                          <p><strong>User:</strong> {testMatchResult.match_result.user_name}</p>
                          <p><strong>Match Type:</strong> {testMatchResult.match_result.match_type}</p>
                          <p><strong>Matched Value:</strong> {testMatchResult.match_result.match_value}</p>
                          <p><strong>Confidence:</strong> {(testMatchResult.match_result.confidence * 100).toFixed(0)}%</p>
                        </div>
                      )}
                      {testMatchResult.extracted_identifiers?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-sm font-medium text-muted-foreground mb-2">Extracted Identifiers:</p>
                          <div className="flex flex-wrap gap-2">
                            {testMatchResult.extracted_identifiers.map((id, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {id.value} ({id.source})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">Failed to load configuration</div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFormat ? 'Edit Decoding Format' : 'Create Decoding Format'}
            </DialogTitle>
            <DialogDescription>
              Define how audio filenames should be parsed to extract call metadata
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Format Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Outbound Call"
                />
              </div>
              <div className="space-y-2">
                <Label>Call Type *</Label>
                <Select 
                  value={formData.call_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, call_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CALL_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className={`h-4 w-4 ${type.color}`} />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Pattern Description</Label>
              <Textarea
                value={formData.pattern_description}
                onChange={(e) => setFormData(prev => ({ ...prev, pattern_description: e.target.value }))}
                placeholder="Describe the format pattern for future reference..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Example Filename *</Label>
              <Input
                value={formData.example_filename}
                onChange={(e) => setFormData(prev => ({ ...prev, example_filename: e.target.value }))}
                placeholder="e.g., 2025_12_01_12_50PM_13103508777_524_Madhuri.mp3"
              />
            </div>

            {/* Segments */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Filename Segments (in order) *</Label>
                <Button variant="outline" size="sm" onClick={addSegment}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Segment
                </Button>
              </div>
              
              {formData.segments.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-lg">
                  <p className="text-muted-foreground text-sm">
                    No segments added. Click &quot;Add Segment&quot; to define the filename structure.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.segments.map((segment, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm font-mono text-muted-foreground w-6">{index + 1}</span>
                      
                      <Select 
                        value={segment.type} 
                        onValueChange={(value) => updateSegment(index, 'type', value)}
                      >
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEGMENT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={segment.parts}
                        onChange={(e) => updateSegment(index, 'parts', parseInt(e.target.value) || 1)}
                        className="w-20"
                        placeholder="Parts"
                      />
                      
                      <Input
                        value={segment.description || ''}
                        onChange={(e) => updateSegment(index, 'description', e.target.value)}
                        placeholder="Description"
                        className="flex-1"
                      />
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSegment(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-muted-foreground">
                <strong>Parts</strong>: Number of underscore-separated parts this segment uses. 
                Date (3 parts): YYYY_MM_DD. Time (2 parts): HH_MMAM.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="btn-primary">
              {isSaving ? 'Saving...' : editingFormat ? 'Update Format' : 'Create Format'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Parser Dialog */}
      <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Test Filename Parser</DialogTitle>
            <DialogDescription>
              Enter a filename to see how it would be parsed using the active formats
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Filename</Label>
              <div className="flex gap-2">
                <Input
                  value={testFilename}
                  onChange={(e) => setTestFilename(e.target.value)}
                  placeholder="e.g., 2025_12_01_12_50PM_13103508777_524_Madhuri.mp3"
                  className="flex-1"
                />
                <Button onClick={handleTest} disabled={isTesting}>
                  {isTesting ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Parse'}
                </Button>
              </div>
            </div>

            {testResult && (
              <div className="space-y-3">
                <Label>Parse Result</Label>
                {testResult.error ? (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    <XCircle className="h-5 w-5" />
                    <span>{testResult.error}</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Successfully parsed using: {testResult.format_used}</span>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <dl className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(testResult).filter(([key]) => !['format_used', 'call_type'].includes(key)).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <dt className="text-muted-foreground capitalize">{key.replace('_', ' ')}:</dt>
                            <dd className="font-medium">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                    <Badge variant="outline" className="gap-1">
                      {getCallTypeIcon(testResult.call_type)}
                      {testResult.call_type === 'outbound' ? 'Outbound Call' : 'Inbound Call'}
                    </Badge>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTestDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
